import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, X, Send, RotateCcw, Sparkles, AlertTriangle, Check, ShieldAlert, CheckCircle2, Mail, RefreshCcw } from "lucide-react";

/**
 * Business Matchmaking Console — Phase 4
 *
 * Renders an 8-tables × 10-time-slots scheduling grid. Click a cell to open
 * the assignment editor: pick a Prime and (optionally) an SBE. Backend
 * prevents double-booking (same prime/sbe in the same time slot on a
 * different table).
 *
 * Time slots:
 *   Formal — Business Matchmaking meetings (Prime ↔ SBE).
 *   Open   — Open Collaboration & Networking. Not auto-populated with
 *            formal meetings; admin can manually place an overflow meeting.
 *
 * Actions:
 *   Assign          — creates a `draft` appointment.
 *   Publish         — flips every `draft` to `published` and fires
 *                     participant emails in the background.
 *   Unpublish       — flips every `published` back to `draft` (rollback).
 *   Auto-fill       — bulk-assigns mutual-interest primes/sbes into empty
 *                     FORMAL cells only; open slots are always skipped.
 *   Prime Focus     — inline panel to schedule one Prime against a ranked
 *                     SBE list (mutual → interested → recommended).
 */
export default function MatchmakingConsole() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cell, setCell] = useState(null); // { table, round }
  const [filterCert, setFilterCert] = useState("");
  const [primeFocusId, setPrimeFocusId] = useState("");
  const [primeFocusData, setPrimeFocusData] = useState(null);
  const [primeFocusLoading, setPrimeFocusLoading] = useState(false);
  const [guardrail, setGuardrail] = useState(null);      // validation result
  const [guardrailOpen, setGuardrailOpen] = useState(false);
  const [guardrailLoading, setGuardrailLoading] = useState(false);
  const [ackAdvisories, setAckAdvisories] = useState(false);
  const [notifications, setNotifications] = useState(null);
  const [notifFilter, setNotifFilter] = useState("all"); // all | sent | failed
  const [notifKind, setNotifKind] = useState("all");      // all | publish | reminder_24h
  const [notifBusy, setNotifBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) { nav("/"); return; }
    load();
  }, [user, loading]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/matchmaking/grid");
      setState(data);
      // Also refresh the notification log (best-effort — endpoint always
      // returns even when the collection is empty).
      loadNotifications();
    } catch { toast.error("Could not load matchmaking data."); }
  };

  const loadNotifications = async () => {
    try {
      const params = {};
      if (notifFilter !== "all") params.status = notifFilter;
      if (notifKind !== "all") params.kind = notifKind;
      const { data } = await api.get("/admin/matchmaking/notifications", { params });
      setNotifications(data);
    } catch { /* silent — empty state is acceptable */ }
  };

  useEffect(() => { if (state) loadNotifications(); }, [notifFilter, notifKind, state]);

  // Mutual-interest set: pairs of (prime_id, sbe_id) that both expressed interest.
  // Computed regardless of load state so hook order stays stable.
  const mutualPairs = useMemo(() => {
    const set = new Set();
    if (!state) return set;
    const bySrcTgt = new Map();
    for (const i of state.interests) {
      const k = `${i.from_exhibitor_id || i.from_participant_id || ""}=>${i.to_exhibitor_id || i.to_participant_id || ""}`;
      bySrcTgt.set(k, true);
    }
    for (const p of state.primes) for (const s of state.sbes) {
      if (bySrcTgt.has(`${p.id}=>${s.id}`) && bySrcTgt.has(`${s.id}=>${p.id}`)) {
        set.add(`${p.id}::${s.id}`);
      }
    }
    return set;
  }, [state]);

  if (loading || !state) return (
    <Layout>
      <section className="container-summit py-24">
        <div className="overline">Loading matchmaking console…</div>
      </section>
    </Layout>
  );

  const { config, rounds, appointments, primes, sbes, published_any } = state;
  const tlMeetings = state.tl_meetings || [];
  const tlByCell = {};
  for (const m of tlMeetings) {
    if (m.table_number != null && m.round_index != null) tlByCell[`${m.table_number}:${m.round_index}`] = m;
  }
  const tablePrefix = config.matchmaking_table_prefix || "M";
  const primesById = Object.fromEntries(primes.map((p) => [p.id, p]));
  const sbesById   = Object.fromEntries(sbes.map((s) => [s.id, s]));
  const byCell = {};
  for (const a of appointments) byCell[`${a.table_number}:${a.round_index}`] = a;

  // Counts for the header.
  const totalDraft = appointments.filter((a) => a.status === "draft").length;
  const totalPub   = appointments.filter((a) => a.status === "published").length;
  const filled     = appointments.length;
  const formalSlotCount = rounds.filter((r) => r.slot_type === "formal").length;
  const capacity   = (config.matchmaking_tables || 8) * formalSlotCount;

  const loadPrimeFocus = async (primeId) => {
    setPrimeFocusId(primeId);
    if (!primeId) { setPrimeFocusData(null); return; }
    setPrimeFocusLoading(true);
    try {
      const { data } = await api.get(`/admin/matchmaking/prime-view/${primeId}`);
      setPrimeFocusData(data);
    } catch { toast.error("Could not load Prime focus view."); }
    finally { setPrimeFocusLoading(false); }
  };

  const openGuardrail = async () => {
    setAckAdvisories(false);
    setGuardrailOpen(true);
    setGuardrailLoading(true);
    setGuardrail(null);
    try {
      const { data } = await api.get("/admin/matchmaking/validate-schedule");
      setGuardrail(data);
    } catch { toast.error("Could not run validation."); setGuardrailOpen(false); }
    finally { setGuardrailLoading(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/matchmaking/publish",
                                       null,
                                       { params: { acknowledged_advisories: true } });
      toast.success(`Published ${data.published} meetings. Notifications are sending in the background.`);
      setGuardrailOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.validation) {
        setGuardrail(detail.validation);
        toast.error(detail.message || "Publish blocked.");
      } else {
        toast.error("Publish failed.");
      }
    } finally { setBusy(false); }
  };
  const unpublish = async () => {
    if (!window.confirm(`Roll ${totalPub} published meeting${totalPub === 1 ? "" : "s"} back to draft?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/matchmaking/unpublish");
      toast.success(`Rolled back ${data.unpublished} meetings.`); await load();
    } catch { toast.error("Unpublish failed."); } finally { setBusy(false); }
  };

  const resendNotification = async (id) => {
    setNotifBusy(true);
    try {
      await api.post(`/admin/matchmaking/notifications/${id}/resend`);
      toast.success("Notification resent.");
      loadNotifications();
    } catch (err) { toast.error(err?.response?.data?.detail || "Resend failed."); }
    finally { setNotifBusy(false); }
  };

  const resendAllFailed = async () => {
    if (!window.confirm("Resend all failed notifications? Each recipient gets exactly one retry email.")) return;
    setNotifBusy(true);
    try {
      const { data } = await api.post("/admin/matchmaking/notifications/resend-failed");
      toast.success(`Resent ${data.resent} of ${data.total_targeted}${data.still_failed ? ` — ${data.still_failed} still failing` : ""}.`);
      loadNotifications();
    } catch { toast.error("Bulk resend failed."); }
    finally { setNotifBusy(false); }
  };

  const autoFill = async () => {
    // Greedy: pair mutual-interest prime↔sbe into empty FORMAL cells only,
    // respecting per-time-slot prime/sbe uniqueness.
    const openIndices = new Set(rounds.filter((r) => r.slot_type === "open").map((r) => r.index));
    const empties = [];
    for (let r = 0; r < rounds.length; r++) {
      if (openIndices.has(r)) continue;
      for (let t = 1; t <= (config.matchmaking_tables || 8); t++)
        if (!byCell[`${t}:${r}`]) empties.push({ table: t, round: r });
    }
    if (!empties.length) return toast.info("No empty formal cells to fill.");
    const pairs = [...mutualPairs].map((k) => { const [p, s] = k.split("::"); return { p, s }; });
    if (!pairs.length) return toast.info("No mutual-interest pairs to auto-fill.");
    const busyPrimeAt = {}; const busySbeAt = {};
    for (const a of appointments) {
      (busyPrimeAt[a.round_index] ||= new Set()).add(a.prime_supplier_id);
      if (a.sbe_id) (busySbeAt[a.round_index] ||= new Set()).add(a.sbe_id);
    }
    const plan = [];
    for (const c of empties) {
      const bp = busyPrimeAt[c.round] || new Set();
      const bs = busySbeAt[c.round] || new Set();
      const hit = pairs.find(({ p, s }) => !bp.has(p) && !bs.has(s));
      if (!hit) continue;
      plan.push({ ...c, p: hit.p, s: hit.s });
      (busyPrimeAt[c.round] ||= new Set()).add(hit.p);
      (busySbeAt[c.round] ||= new Set()).add(hit.s);
      pairs.splice(pairs.indexOf(hit), 1);
    }
    if (!plan.length) return toast.info("No compatible pairs fit into empty formal cells.");
    if (!window.confirm(`Auto-fill will create ${plan.length} draft meetings from mutual interest. Open Collaboration slots are skipped. Continue?`)) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const item of plan) {
      const sbe = sbesById[item.s];
      try {
        await api.post("/admin/matchmaking/assign", {
          table_number: item.table, round_index: item.round,
          prime_supplier_id: item.p, sbe_id: item.s,
          sbe_name: sbe?.name, sbe_company: sbe?.company, sbe_email: sbe?.email,
        });
        ok++;
      } catch { fail++; }
    }
    toast.success(`Auto-filled ${ok} meetings${fail ? ` (${fail} skipped)` : ""}.`);
    setBusy(false);
    await load();
  };

  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))]">
        <div className="container-summit py-10">
          <Link to="/admin" className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black inline-flex items-center gap-1" data-testid="mm-back">
            <ArrowLeft size={12} /> All Workspaces
          </Link>
          <div className="mt-4 flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="overline"><span className="accent-rule" />Business Matchmaking</div>
              <h1 className="text-4xl sm:text-5xl mt-3">Matchmaking Console</h1>
              <p className="text-sm text-zinc-600 max-w-2xl mt-3">
                {config.matchmaking_tables} tables · {rounds.length} time slots ({formalSlotCount} formal + {rounds.length - formalSlotCount} open collaboration) · {config.matchmaking_slot_minutes}-minute meetings starting at{" "}
                {new Date(config.matchmaking_start_iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET.
                Click a cell to assign a Prime + SBE. Publish when the schedule is final.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Kpi label="Filled"    value={`${filled} / ${capacity}`} />
              <Kpi label="Drafts"    value={totalDraft} kind="amber" />
              <Kpi label="Published" value={totalPub} kind={totalPub ? "green" : "muted"} />
            </div>
          </div>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <Link to="/admin/matchmaking/runbook"
                  className="text-[11px] uppercase tracking-[0.16em] border border-[#A39171] text-[#7A6A4A] bg-[#FBF8F1] hover:bg-[#F1E9D2] px-3 py-2 inline-flex items-center gap-2"
                  data-testid="mm-runbook-link">
              <Sparkles size={12} /> Open the Runbook
            </Link>
            <Link to="/admin/matchmaking/print"
                  className="text-[11px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-3 py-2 inline-flex items-center gap-2"
                  data-testid="mm-print-all-link">
              Print All Itineraries
            </Link>
            <Link to="/runsheet"
                  className="text-[11px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-3 py-2 inline-flex items-center gap-2"
                  data-testid="mm-runsheet-link">
              Day-Of Run Sheet
            </Link>
            <button onClick={autoFill} disabled={busy}
                    className="text-[11px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-3 py-2 inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="mm-autofill">
              <Sparkles size={12} /> Auto-fill from Mutual Interest
            </button>
            <button onClick={openGuardrail} disabled={busy || totalDraft === 0}
                    className="text-[11px] uppercase tracking-[0.16em] border border-black bg-black text-white hover:bg-zinc-800 px-3 py-2 inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="mm-publish">
              <Send size={12} /> Publish {totalDraft > 0 && `(${totalDraft})`}
            </button>
            {published_any && (
              <button onClick={unpublish} disabled={busy}
                      className="text-[11px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-3 py-2 inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="mm-unpublish">
                <RotateCcw size={12} /> Roll Back to Draft
              </button>
            )}
            {(primes.length === 0 || sbes.length === 0) && (
              <div className="text-[11px] uppercase tracking-[0.16em] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 inline-flex items-center gap-2">
                <AlertTriangle size={12} />
                {primes.length === 0 ? "No approved Prime Suppliers yet — approve some to start scheduling." : "No SBE participants yet."}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="container-summit py-8">
        {/* ---- Prime Focus Panel ---- */}
        <PrimeFocusPanel
          primes={primes}
          primeFocusId={primeFocusId}
          primeFocusData={primeFocusData}
          primeFocusLoading={primeFocusLoading}
          onSelect={loadPrimeFocus}
          onAssign={(sbe) => {
            if (!primeFocusData?.available_formal_slots?.length) {
              return toast.info("No available formal slots for this Prime.");
            }
            // Deep-link into the CellEditor pre-populated with this Prime + SBE.
            const slot = primeFocusData.available_formal_slots[0];
            // Find first empty table for that slot.
            const tables = config.matchmaking_tables || 8;
            let table = null;
            for (let t = 1; t <= tables; t++) {
              if (!byCell[`${t}:${slot.index}`]) { table = t; break; }
            }
            if (!table) return toast.info("All tables are taken at the next available time slot.");
            setCell({ table, round: slot.index, preselect: { prime_id: primeFocusId, sbe_id: sbe.id } });
          }}
        />

        {/* ---- Grid ---- */}
        <div className="overflow-x-auto">
          <table className="min-w-full border border-zinc-200 text-xs" data-testid="mm-grid">
            <thead className="bg-zinc-50">
              <tr>
                <th className="border-b border-r border-zinc-200 px-2 py-3 text-left uppercase tracking-[0.16em] text-[10px] text-zinc-500 sticky left-0 bg-zinc-50 z-10">Time Slot</th>
                {Array.from({ length: config.matchmaking_tables || 8 }, (_, i) => i + 1).map((t) => (
                  <th key={t} className="border-b border-r border-zinc-200 px-2 py-3 text-center uppercase tracking-[0.16em] text-[10px] text-zinc-500 min-w-[110px]">
                    {tablePrefix}-{t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rounds.map((r) => {
                const isOpen = r.slot_type === "open";
                return (
                <tr key={r.index} className={isOpen ? "bg-[#FBF8F1]/40" : ""}>
                  <td className="border-b border-r border-zinc-200 px-2 py-2 text-[11px] font-medium sticky left-0 bg-white z-10">
                    <div className="text-zinc-900">Slot {r.index + 1}</div>
                    <div className="text-[10px] text-zinc-500">{r.label}</div>
                    {isOpen && (
                      <div className="mt-1 text-[9px] uppercase tracking-[0.16em] text-[#7A6A4A]">Open Collab</div>
                    )}
                  </td>
                  {Array.from({ length: config.matchmaking_tables || 8 }, (_, i) => i + 1).map((t) => {
                    const a = byCell[`${t}:${r.index}`];
                    const tl = !a && tlByCell[`${t}:${r.index}`];
                    if (tl) {
                      return (
                        <td key={t} className="border-b border-r border-zinc-200 p-0 align-top bg-indigo-50/50">
                          <button
                            onClick={() => toast.info("T+L sourcing meetings are managed in Event Ops → Matchmaking tab.")}
                            className="w-full h-full min-h-[64px] text-left px-2 py-2"
                            data-testid={`mm-cell-tl-${t}-${r.index}`}
                          >
                            <div className="font-medium truncate text-[11px]">{tl.tl_leader_name}</div>
                            <div className="text-[10px] text-zinc-500 truncate">
                              {tl.prime_supplier?.company_name || tl.sbe?.company || tl.sbe_name || "—"}
                            </div>
                            <div className="mt-1">
                              <span className="text-[9px] uppercase tracking-[0.16em] px-1 py-[1px] border border-indigo-300 text-indigo-700 bg-indigo-50">T+L Sourcing</span>
                            </div>
                          </button>
                        </td>
                      );
                    }
                    return (
                      <td key={t} className={`border-b border-r border-zinc-200 p-0 align-top ${isOpen ? "bg-[#FBF8F1]/60" : ""}`}>
                        <button
                          onClick={() => setCell({ table: t, round: r.index, appointment: a })}
                          className={`w-full h-full min-h-[64px] text-left px-2 py-2 hover:bg-zinc-50 transition ${a ? (a.status === "published" ? "bg-emerald-50/50" : "bg-amber-50/40") : ""}`}
                          data-testid={`mm-cell-${t}-${r.index}`}
                        >
                          {a ? (
                            <>
                              <div className="font-medium truncate text-[11px]">
                                {primesById[a.prime_supplier_id]?.company_name || "Unknown Prime"}
                              </div>
                              <div className="text-[10px] text-zinc-500 truncate">
                                {a.sbe_id ? (sbesById[a.sbe_id]?.company || sbesById[a.sbe_id]?.name || a.sbe_company || a.sbe_name) : (a.sbe_company || a.sbe_name || "— Open —")}
                              </div>
                              <div className="mt-1">
                                <span className={`text-[9px] uppercase tracking-[0.16em] px-1 py-[1px] border ${a.status === "published" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                                  {a.status}
                                </span>
                              </div>
                            </>
                          ) : (
                            isOpen ? (
                              <span className="text-[10px] text-[#7A6A4A]/60 italic">Open</span>
                            ) : (
                              <span className="text-zinc-300 text-lg leading-none">+</span>
                            )
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );})}
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid md:grid-cols-5 gap-4 text-[11px] text-zinc-500">
          <Legend swatch="bg-amber-50 border-amber-200" label="Draft — not yet visible to suppliers" />
          <Legend swatch="bg-emerald-50 border-emerald-200" label="Published — in personal itineraries" />
          <Legend swatch="bg-indigo-50 border-indigo-200" label="T+L Sourcing — managed in Event Ops" />
          <Legend swatch="bg-[#FBF8F1] border-[#E8DFC8]" label="Open Collaboration & Networking — not auto-filled" />
          <Legend swatch="bg-white border-zinc-200" label="Empty formal slot — click to assign" />
        </div>

        {/* T+L sourcing meetings — station meetings don't occupy grid cells, so list them all here */}
        {tlMeetings.length > 0 && (
          <div className="mt-8 card-flat p-5" data-testid="mm-console-tl-meetings">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="overline">T+L Sourcing Meetings ({tlMeetings.length})</div>
              <div className="flex items-center gap-4">
                <Link to="/admin/matchmaking/print" className="text-[11px] uppercase tracking-[0.16em] underline underline-offset-4 hover:text-black text-zinc-500" data-testid="mm-console-print-link">
                  Print All Itineraries
                </Link>
                <Link to="/admin/event-ops" className="text-[11px] uppercase tracking-[0.16em] underline underline-offset-4 hover:text-black text-zinc-500">
                  Manage in Event Ops → Matchmaking
                </Link>
              </div>
            </div>
            <ul className="mt-3 grid md:grid-cols-2 gap-2">
              {tlMeetings.map((m) => (
                <li key={m.id} className="border border-zinc-200 px-3 py-2 text-[12px]" data-testid={`mm-console-tl-${m.id}`}>
                  <span className="font-medium">{m.tl_leader_name}</span>
                  <span className="text-zinc-400"> × </span>
                  <span>{m.prime_supplier?.company_name || m.sbe?.company || m.sbe_name || "—"}</span>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {m.start_iso && new Date(m.start_iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })}
                    {m.end_iso && ` – ${new Date(m.end_iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET`}
                    {" · "}{m.location}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Post-publish notification analytics */}
      <NotificationsPanel
        data={notifications}
        filter={notifFilter} setFilter={setNotifFilter}
        kind={notifKind} setKind={setNotifKind}
        busy={notifBusy}
        onResend={resendNotification}
        onResendAllFailed={resendAllFailed}
      />

      {cell && (
        <CellEditor
          cell={cell} onClose={() => setCell(null)}
          primes={primes} sbes={sbes} appointments={appointments}
          rounds={rounds} mutualPairs={mutualPairs}
          filterCert={filterCert} setFilterCert={setFilterCert}
          onSaved={async () => { setCell(null); await load(); }}
        />
      )}

      {guardrailOpen && (
        <GuardrailDrawer
          onClose={() => setGuardrailOpen(false)}
          loading={guardrailLoading}
          v={guardrail}
          busy={busy}
          totalDraft={totalDraft}
          ackAdvisories={ackAdvisories}
          setAckAdvisories={setAckAdvisories}
          onPublish={publish}
        />
      )}
    </Layout>
  );
}

/* ============================================================
 * Cell editor drawer
 * ============================================================ */
function CellEditor({ cell, onClose, primes, sbes, appointments, rounds, mutualPairs, filterCert, setFilterCert, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [primeId, setPrimeId] = useState(cell.appointment?.prime_supplier_id || cell.preselect?.prime_id || "");
  const [sbeId,   setSbeId]   = useState(cell.appointment?.sbe_id || cell.preselect?.sbe_id || "");
  const [notes,   setNotes]   = useState(cell.appointment?.notes || "");
  const round = rounds[cell.round];
  const isOpenSlot = round?.slot_type === "open";

  // Which primes/sbes are already occupied this round?
  const primesBusy = new Set(appointments.filter((a) => a.round_index === cell.round && a.id !== cell.appointment?.id).map((a) => a.prime_supplier_id));
  const sbesBusy   = new Set(appointments.filter((a) => a.round_index === cell.round && a.id !== cell.appointment?.id && a.sbe_id).map((a) => a.sbe_id));

  const availPrimes = primes.filter((p) => !primesBusy.has(p.id));
  const availSbes   = sbes.filter((s) => !sbesBusy.has(s.id))
                          .filter((s) => !filterCert || (s.certifications || []).includes(filterCert));

  // Sort available SBEs so mutual-interest partners for the selected Prime appear first.
  const sortedSbes = useMemo(() => {
    const withScore = availSbes.map((s) => ({ s, mutual: !!primeId && mutualPairs.has(`${primeId}::${s.id}`) }));
    withScore.sort((a, b) => (b.mutual ? 1 : 0) - (a.mutual ? 1 : 0) || (a.s.company || "").localeCompare(b.s.company || ""));
    return withScore;
  }, [primeId, availSbes, mutualPairs]);

  const save = async () => {
    if (!primeId) return toast.error("Select a Prime Supplier.");
    setSaving(true);
    const sbe = sbes.find((s) => s.id === sbeId);
    const payload = {
      table_number: cell.table, round_index: cell.round,
      prime_supplier_id: primeId,
      sbe_id: sbeId || null,
      sbe_name: sbe?.name || null,
      sbe_company: sbe?.company || null,
      sbe_email: sbe?.email || null,
      notes: notes || null,
    };
    try {
      if (cell.appointment) {
        await api.patch(`/admin/matchmaking/assign/${cell.appointment.id}`, {
          prime_supplier_id: payload.prime_supplier_id,
          sbe_id: payload.sbe_id, sbe_name: payload.sbe_name,
          sbe_company: payload.sbe_company, sbe_email: payload.sbe_email, notes: payload.notes,
        });
        toast.success("Meeting updated.");
      } else {
        await api.post("/admin/matchmaking/assign", payload);
        toast.success("Meeting scheduled.");
      }
      await onSaved();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not save meeting.";
      toast.error(msg);
    } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!cell.appointment) return;
    if (!window.confirm("Delete this meeting?")) return;
    setSaving(true);
    try {
      await api.delete(`/admin/matchmaking/assign/${cell.appointment.id}`);
      toast.success("Meeting deleted.");
      await onSaved();
    } catch { toast.error("Delete failed."); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mm-editor">
        <div className="border-b border-zinc-200 p-5 flex items-start justify-between gap-4">
          <div>
            <div className="overline">Table M-{cell.table} · Time Slot {cell.round + 1}</div>
            <div className="text-lg font-medium mt-1">{round?.label}</div>
            {isOpenSlot && (
              <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#7A6A4A]">Open Collaboration & Networking · overflow only</div>
            )}
            {cell.appointment && (
              <div className="mt-1">
                <span className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 border ${cell.appointment.status === "published" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                  {cell.appointment.status}
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-black" aria-label="Close" data-testid="mm-editor-close"><X size={18} /></button>
        </div>

        <div className="p-5 grid gap-5">
          <div>
            <Label className="overline flex items-center justify-between">
              <span>Prime Supplier</span>
              {primesBusy.size > 0 && <span className="text-[10px] text-zinc-500 normal-case tracking-normal">{primesBusy.size} busy this slot</span>}
            </Label>
            <Select value={primeId} onValueChange={setPrimeId}>
              <SelectTrigger className="h-11 rounded-none mt-2" data-testid="mm-editor-prime"><SelectValue placeholder="Select a Prime…" /></SelectTrigger>
              <SelectContent>
                {availPrimes.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.company_name} {p.booth_number ? `· ${p.booth_number}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="overline flex items-center justify-between">
              <span>SBE (optional)</span>
              <button type="button" onClick={() => setSbeId("")} className="text-[10px] text-zinc-500 hover:text-black normal-case tracking-normal">Clear</button>
            </Label>
            <div className="mt-2 grid gap-2">
              <Input placeholder="Filter by certification (e.g. MBE, WBE)…"
                     value={filterCert} onChange={(e) => setFilterCert(e.target.value)}
                     className="h-9 rounded-none" data-testid="mm-editor-cert-filter" />
              <div className="border border-zinc-200 max-h-64 overflow-y-auto">
                {sortedSbes.length === 0 && (
                  <div className="p-3 text-sm text-zinc-500">No SBEs available this slot.</div>
                )}
                {sortedSbes.map(({ s, mutual }) => (
                  <button key={s.id} type="button"
                          onClick={() => setSbeId(s.id)}
                          className={`w-full text-left px-3 py-2 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 ${sbeId === s.id ? "bg-black text-white hover:bg-black" : ""}`}
                          data-testid={`mm-editor-sbe-${s.id}`}>
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      <span className="truncate">{s.company || s.name}</span>
                      {mutual && <span className={`text-[9px] uppercase tracking-[0.16em] px-1 py-[1px] border ${sbeId === s.id ? "border-white text-white" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>Mutual</span>}
                    </div>
                    <div className={`text-[10px] truncate ${sbeId === s.id ? "text-zinc-300" : "text-zinc-500"}`}>
                      {s.name || s.email}{(s.certifications || []).length ? ` · ${(s.certifications || []).join(", ")}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label className="overline">Notes (internal)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none mt-2" data-testid="mm-editor-notes" />
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-zinc-100">
            <button onClick={save} disabled={saving || !primeId}
                    className="btn-black flex-1" data-testid="mm-editor-save">
              <Check size={14} /> {cell.appointment ? "Update Meeting" : "Schedule Meeting"}
            </button>
            {cell.appointment && (
              <button onClick={remove} disabled={saving}
                      className="text-[11px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-red-500 hover:text-red-600 px-3 py-2"
                      data-testid="mm-editor-delete">Delete</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * Prime Focus Panel — pick a Prime, see ranked SBEs and unbooked slots.
 * Purpose: build high-quality schedules quickly without opening each
 * SBE profile individually. Ranking (server-computed):
 *   MUTUAL INTEREST > PRIME INTERESTED > SBE INTERESTED > RECOMMENDED
 * ============================================================ */
function PrimeFocusPanel({ primes, primeFocusId, primeFocusData, primeFocusLoading, onSelect, onAssign }) {
  return (
    <section className="mb-6 border border-zinc-200 bg-white" data-testid="mm-prime-focus">
      <header className="border-b border-zinc-200 px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="overline">Prime Focus · Ranked SBE Suggestions</div>
        <select value={primeFocusId} onChange={(e) => onSelect(e.target.value)}
                data-testid="mm-prime-focus-select"
                className="h-9 px-3 border border-zinc-300 text-sm rounded-none focus:outline-none focus:border-black flex-1 min-w-[240px]">
          <option value="">Select a Prime Supplier…</option>
          {primes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.company_name} {p.booth_number ? `· ${p.booth_number}` : ""}
            </option>
          ))}
        </select>
      </header>
      {primeFocusLoading && (
        <div className="p-4 text-xs text-zinc-500">Loading Prime overview…</div>
      )}
      {!primeFocusLoading && primeFocusData && (
        <div className="p-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          {/* LEFT: prime summary */}
          <div className="border-r border-zinc-100 md:pr-4">
            <div className="text-[15px] font-medium" data-testid="mm-prime-focus-name">{primeFocusData.prime.company_name}</div>
            <div className="text-[11px] text-zinc-500 mt-1">
              {primeFocusData.prime.primary_contact}
              {primeFocusData.prime.booth_number ? ` · ${primeFocusData.prime.booth_number}` : ""}
            </div>
            {primeFocusData.prime.industry_category && (
              <div className="text-[11px] mt-3 uppercase tracking-[0.16em] text-zinc-500">Sourcing Category</div>
            )}
            {primeFocusData.prime.industry_category && (
              <div className="text-sm mt-1">{primeFocusData.prime.industry_category}</div>
            )}
            {primeFocusData.prime.matchmaking_preferences?.length > 0 && (
              <>
                <div className="text-[11px] mt-3 uppercase tracking-[0.16em] text-zinc-500">Matchmaking Interests</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {primeFocusData.prime.matchmaking_preferences.map((v) => (
                    <span key={v} className="text-[10px] uppercase tracking-[0.14em] border border-zinc-300 px-1.5 py-0.5 text-zinc-600">{v}</span>
                  ))}
                </div>
              </>
            )}
            <div className="mt-4 grid grid-cols-3 gap-2">
              <FocusStat label="Scheduled" value={primeFocusData.meetings_scheduled} testId="mm-focus-scheduled" />
              <FocusStat label="Allowance" value={primeFocusData.max_meetings} />
              <FocusStat label="Open Slots" value={primeFocusData.available_formal_slots.length} testId="mm-focus-open" />
            </div>
            {primeFocusData.available_formal_slots.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Available Formal Time Slots</div>
                <div className="text-[11px] text-zinc-700 space-y-0.5" data-testid="mm-focus-available-slots">
                  {primeFocusData.available_formal_slots.map((s) => (
                    <div key={s.index}>Slot {s.index + 1} · {s.label}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* RIGHT: ranked SBE list */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
              Ranked SBEs — mutual interest first, then capability alignment
            </div>
            <div className="border border-zinc-200 max-h-96 overflow-y-auto" data-testid="mm-focus-sbes">
              {primeFocusData.ranked_sbes.length === 0 && (
                <div className="p-3 text-sm text-zinc-500">No SBEs registered yet.</div>
              )}
              {primeFocusData.ranked_sbes.map((s) => (
                <div key={s.id} className={`px-3 py-2 border-b border-zinc-100 flex items-center gap-3 ${s.already_scheduled_with_prime ? "bg-zinc-50" : ""}`}
                     data-testid={`mm-focus-sbe-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      <span className="truncate">{s.company}</span>
                      {s.mutual_interest && <Chip color="emerald">Mutual Interest</Chip>}
                      {!s.mutual_interest && s.prime_interested && <Chip color="amber">Prime Interested</Chip>}
                      {!s.mutual_interest && !s.prime_interested && s.sbe_interested && <Chip color="amber">SBE Interested</Chip>}
                      {s.recommended && !s.mutual_interest && !s.prime_interested && !s.sbe_interested && <Chip color="zinc">Recommended</Chip>}
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate mt-0.5">
                      Capability overlap: {s.capability_overlap} · Scheduled today: {s.sbe_meetings_scheduled}
                      {s.already_scheduled_with_prime && " · already with this Prime"}
                    </div>
                  </div>
                  <button
                    onClick={() => onAssign(s)}
                    disabled={s.already_scheduled_with_prime}
                    data-testid={`mm-focus-assign-${s.id}`}
                    className="text-[10px] uppercase tracking-[0.16em] border border-black hover:bg-black hover:text-white px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {s.already_scheduled_with_prime ? "Scheduled" : "Assign"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function FocusStat({ label, value, testId }) {
  return (
    <div className="border border-zinc-200 px-2 py-1.5" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-[0.16em] text-zinc-500">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}

function Chip({ color, children }) {
  const cls = {
    emerald: "border-emerald-300 text-emerald-700 bg-emerald-50",
    amber:   "border-amber-300 text-amber-700 bg-amber-50",
    zinc:    "border-zinc-300 text-zinc-700 bg-zinc-50",
  }[color] || "border-zinc-300 text-zinc-700";
  return (
    <span className={`text-[9px] uppercase tracking-[0.16em] px-1 py-[1px] border ${cls}`}>{children}</span>
  );
}

/* ============================================================
 * Small presentational bits
 * ============================================================ */
function Kpi({ label, value, kind = "default" }) {
  const cls = {
    default: "border-zinc-300 text-zinc-900",
    amber:   "border-amber-300 text-amber-700 bg-amber-50",
    green:   "border-emerald-300 text-emerald-700 bg-emerald-50",
    muted:   "border-zinc-200 text-zinc-400",
  }[kind];
  return (
    <div className={`border ${cls} px-4 py-2 min-w-[110px]`}>
      <div className="text-[10px] uppercase tracking-[0.18em]">{label}</div>
      <div className="text-xl font-medium mt-0.5">{value}</div>
    </div>
  );
}
function Legend({ swatch, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-4 h-4 border ${swatch}`} />
      <span>{label}</span>
    </div>
  );
}


/* ============================================================
 * Guardrail Drawer — pre-publish schedule validation
 * ------------------------------------------------------------
 * Hard conflicts (participant/table double-booking) block publish.
 * Advisories (below target, zero meetings, unassigned mutual matches,
 * meetings in Open Collaboration slots) are surfaced prominently but
 * an admin may proceed after ticking the acknowledgment checkbox.
 * ============================================================ */
function GuardrailDrawer({ onClose, loading, v, busy, totalDraft, ackAdvisories, setAckAdvisories, onPublish }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mm-guardrail">
        <div className="border-b border-zinc-200 p-5 flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
          <div>
            <div className="overline">Pre-Publish Guardrail</div>
            <div className="text-xl font-medium mt-1">Review before publishing</div>
            <p className="text-xs text-zinc-500 mt-1">Publishing {totalDraft} draft meeting{totalDraft === 1 ? "" : "s"}. Participants receive an email immediately after publish.</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-black" aria-label="Close" data-testid="mm-guardrail-close"><X size={18} /></button>
        </div>

        {loading || !v ? (
          <div className="p-6 text-sm text-zinc-500">Running validation…</div>
        ) : (
          <div className="p-5 grid gap-5">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryStat label="Total Scheduled" value={v.totals.scheduled} testId="grd-total"/>
              <SummaryStat label="Drafts" value={v.totals.draft} testId="grd-drafts"/>
              <SummaryStat label="Fair Target" value={`${v.totals.min_meetings_target} mtgs`} />
              <SummaryStat label="Overall"
                            value={v.hard_conflicts ? "Blocked" : (v.advisories ? "Advisories" : "Clear")}
                            kind={v.hard_conflicts ? "red" : (v.advisories ? "amber" : "green")}
                            testId="grd-overall"/>
            </div>

            {/* HARD */}
            {(v.hard.participant_double_bookings.length > 0 || v.hard.table_double_bookings.length > 0) && (
              <IssueBlock kind="hard" title="Hard Conflicts — must be resolved before publishing" testId="grd-hard">
                {v.hard.participant_double_bookings.map((r, i) => (
                  <IssueRow key={`pd-${i}`} testId={`grd-pd-${i}`}
                            title={`${r.role === "prime" ? "Prime" : "SBE"} double-booked · ${r.company || "—"}`}
                            detail={`Scheduled in the SAME time slot on two different tables · Slot ${r.round_index + 1} · ${r.slot_label}`} />
                ))}
                {v.hard.table_double_bookings.map((r, i) => (
                  <IssueRow key={`td-${i}`} testId={`grd-td-${i}`}
                            title={`Table M-${r.table_number} double-booked`}
                            detail={`Two appointments at the same table + time slot · Slot ${r.round_index + 1} · ${r.slot_label}`} />
                ))}
              </IssueBlock>
            )}

            {/* ADVISORIES */}
            {v.advisory.zero_meeting_primes.length > 0 && (
              <IssueBlock kind="advisory" title={`${v.advisory.zero_meeting_primes.length} Prime${v.advisory.zero_meeting_primes.length === 1 ? "" : "s"} with zero meetings`} testId="grd-zero-primes">
                {v.advisory.zero_meeting_primes.slice(0, 30).map((p) => (
                  <IssueRow key={p.id} testId={`grd-zp-${p.id}`}
                            title={p.company || "—"}
                            detail={p.contact ? `${p.contact} · 0 meetings scheduled` : "0 meetings scheduled"} />
                ))}
              </IssueBlock>
            )}
            {v.advisory.zero_meeting_sbes.length > 0 && (
              <IssueBlock kind="advisory" title={`${v.advisory.zero_meeting_sbes.length} SBE${v.advisory.zero_meeting_sbes.length === 1 ? "" : "s"} with zero meetings`} testId="grd-zero-sbes">
                {v.advisory.zero_meeting_sbes.slice(0, 30).map((s) => (
                  <IssueRow key={s.id} testId={`grd-zs-${s.id}`}
                            title={s.company || "—"}
                            detail={s.name || s.email || "0 meetings scheduled"} />
                ))}
              </IssueBlock>
            )}
            {v.advisory.below_target_primes.length > 0 && (
              <IssueBlock kind="advisory" title={`${v.advisory.below_target_primes.length} Prime${v.advisory.below_target_primes.length === 1 ? "" : "s"} below fair-participation target`} testId="grd-below-primes">
                {v.advisory.below_target_primes.slice(0, 30).map((p) => (
                  <IssueRow key={p.id} testId={`grd-bp-${p.id}`}
                            title={p.company || "—"}
                            detail={`${p.meetings_scheduled} of ${p.target} target meetings scheduled`} />
                ))}
              </IssueBlock>
            )}
            {v.advisory.below_target_sbes.length > 0 && (
              <IssueBlock kind="advisory" title={`${v.advisory.below_target_sbes.length} SBE${v.advisory.below_target_sbes.length === 1 ? "" : "s"} below fair-participation target`} testId="grd-below-sbes">
                {v.advisory.below_target_sbes.slice(0, 30).map((s) => (
                  <IssueRow key={s.id} testId={`grd-bs-${s.id}`}
                            title={s.company || "—"}
                            detail={`${s.meetings_scheduled} of ${s.target} target meetings scheduled`} />
                ))}
              </IssueBlock>
            )}
            {v.advisory.unassigned_mutual_matches.length > 0 && (
              <IssueBlock kind="advisory" title={`${v.advisory.unassigned_mutual_matches.length} unassigned mutual match${v.advisory.unassigned_mutual_matches.length === 1 ? "" : "es"}`} testId="grd-mutual">
                {v.advisory.unassigned_mutual_matches.slice(0, 30).map((r, i) => (
                  <IssueRow key={`mm-${i}`} testId={`grd-mm-${i}`}
                            title={`${r.prime_company || "—"}  ↔  ${r.sbe_company || "—"}`}
                            detail="Both sides expressed interest but no meeting is scheduled" />
                ))}
              </IssueBlock>
            )}
            {v.advisory.open_slot_placements.length > 0 && (
              <IssueBlock kind="advisory" title={`${v.advisory.open_slot_placements.length} meeting${v.advisory.open_slot_placements.length === 1 ? "" : "s"} in Open Collaboration slot${v.advisory.open_slot_placements.length === 1 ? "" : "s"}`} testId="grd-open">
                {v.advisory.open_slot_placements.map((r, i) => (
                  <IssueRow key={`osp-${i}`} testId={`grd-osp-${i}`}
                            title={`${r.prime_company || "—"} · Table M-${r.table_number}`}
                            detail={`Slot ${r.round_index + 1} · ${r.slot_label} · ${r.sbe_company ? `with ${r.sbe_company}` : "no SBE assigned"}`} />
                ))}
              </IssueBlock>
            )}

            {!v.hard_conflicts && !v.advisories && (
              <div className="border border-emerald-200 bg-emerald-50 p-4 rounded-none flex items-start gap-3" data-testid="grd-clear">
                <CheckCircle2 size={16} className="text-emerald-700 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-emerald-900">All clear</div>
                  <div className="text-xs text-emerald-800/80 mt-1">No conflicts or advisories detected. Safe to publish.</div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-zinc-100 pt-4 grid gap-3">
              {v.advisories && !v.hard_conflicts && (
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="grd-ack-label">
                  <input type="checkbox" checked={ackAdvisories}
                         onChange={(e) => setAckAdvisories(e.target.checked)}
                         data-testid="grd-ack" className="mt-1" />
                  <span>I&apos;ve reviewed the advisories above and want to publish anyway.</span>
                </label>
              )}
              <div className="flex items-center gap-3">
                <button onClick={onPublish}
                        disabled={busy || v.hard_conflicts || (v.advisories && !ackAdvisories)}
                        className="btn-black flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="grd-publish">
                  <Send size={14} /> Publish {totalDraft > 0 ? `(${totalDraft})` : ""}
                </button>
                <button onClick={onClose}
                        className="text-[11px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-3 py-2"
                        data-testid="grd-cancel">Cancel</button>
              </div>
              {v.hard_conflicts && (
                <div className="text-[11px] text-red-700 flex items-center gap-1.5" data-testid="grd-blocked-msg">
                  <ShieldAlert size={12} /> Publish is blocked until every hard conflict is resolved.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, kind = "default", testId }) {
  const cls = {
    default: "border-zinc-300 text-zinc-900",
    green: "border-emerald-300 text-emerald-800 bg-emerald-50",
    amber: "border-amber-300 text-amber-800 bg-amber-50",
    red: "border-red-300 text-red-800 bg-red-50",
  }[kind];
  return (
    <div className={`border ${cls} px-3 py-2`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em]">{label}</div>
      <div className="text-lg font-medium mt-0.5">{value}</div>
    </div>
  );
}

function IssueBlock({ kind, title, children, testId }) {
  const cls = kind === "hard"
    ? "border-red-200 bg-red-50/50"
    : "border-amber-200 bg-amber-50/40";
  const Icon = kind === "hard" ? ShieldAlert : AlertTriangle;
  const iconCls = kind === "hard" ? "text-red-700" : "text-amber-700";
  return (
    <div className={`border ${cls}`} data-testid={testId}>
      <div className="px-4 py-2.5 border-b border-inherit flex items-center gap-2">
        <Icon size={14} className={iconCls} />
        <div className="text-[11px] uppercase tracking-[0.16em] font-medium">{title}</div>
      </div>
      <div className="divide-y divide-white/60">{children}</div>
    </div>
  );
}

function IssueRow({ title, detail, testId }) {
  return (
    <div className="px-4 py-2" data-testid={testId}>
      <div className="text-sm font-medium">{title}</div>
      <div className="text-[11px] text-zinc-600 mt-0.5">{detail}</div>
    </div>
  );
}

/* ============================================================
 * NotificationsPanel — post-publish delivery log + resend
 * ============================================================ */
function NotificationsPanel({ data, filter, setFilter, kind, setKind, busy, onResend, onResendAllFailed }) {
  const items = data?.notifications || [];
  const counts = data?.counts || { total: 0, sent: 0, failed: 0, publish: 0, reminder_24h: 0 };
  const hasAny = counts.total > 0;
  const failedCount = counts.failed;

  return (
    <section className="container-summit pb-16" data-testid="mm-notifications">
      <div className="border border-zinc-200 bg-white">
        <div className="border-b border-zinc-200 p-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="overline flex items-center gap-2"><Mail size={12} className="text-zinc-500" /> Notification Log</div>
            <div className="text-xs text-zinc-500 mt-1">Emails dispatched to participants after Publish or the T-24h reminder.</div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <SummaryStat label="Total" value={counts.total} />
            <SummaryStat label="Sent" value={counts.sent} kind={counts.sent ? "green" : "default"} />
            <SummaryStat label="Failed" value={counts.failed} kind={counts.failed ? "red" : "default"} testId="mm-notif-failed-count"/>
            <SummaryStat label="Publish · 24h" value={`${counts.publish} · ${counts.reminder_24h}`} />
          </div>
        </div>
        <div className="border-b border-zinc-200 p-3 flex items-center gap-3 flex-wrap">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  data-testid="mm-notif-filter-status"
                  className="h-9 px-3 border border-zinc-300 text-xs rounded-none focus:outline-none focus:border-black">
            <option value="all">All statuses</option>
            <option value="sent">Sent only</option>
            <option value="failed">Failed only</option>
          </select>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
                  data-testid="mm-notif-filter-kind"
                  className="h-9 px-3 border border-zinc-300 text-xs rounded-none focus:outline-none focus:border-black">
            <option value="all">All kinds</option>
            <option value="publish">Publish</option>
            <option value="reminder_24h">24h Reminder</option>
          </select>
          <div className="flex-1" />
          {failedCount > 0 && (
            <button onClick={onResendAllFailed} disabled={busy}
                    className="text-[11px] uppercase tracking-[0.16em] border border-black bg-black text-white hover:bg-zinc-800 px-3 py-2 inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="mm-notif-resend-all">
              <RefreshCcw size={12} /> Resend All Failed ({failedCount})
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          {!hasAny ? (
            <div className="p-8 text-sm text-zinc-500 text-center" data-testid="mm-notif-empty">
              No notifications yet. Publish a schedule to start the delivery log.
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 text-center">No notifications match the current filter.</div>
          ) : (
            <table className="min-w-full text-xs" data-testid="mm-notif-table">
              <thead className="bg-zinc-50">
                <tr className="text-left uppercase tracking-[0.16em] text-[10px] text-zinc-500">
                  <th className="px-3 py-2.5">Recipient</th>
                  <th className="px-3 py-2.5">Company</th>
                  <th className="px-3 py-2.5">Role</th>
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">Kind</th>
                  <th className="px-3 py-2.5">Attempt</th>
                  <th className="px-3 py-2.5">Sent at</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((n) => (
                  <tr key={n.id} data-testid={`mm-notif-row-${n.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{n.name || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{n.company || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                      {n.role === "prime" ? "Prime" : n.role === "prime_addl_rep" ? "Prime · Addl Rep" : "SBE"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-zinc-700">{n.email}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                      {n.kind === "reminder_24h" ? "24h" : "Publish"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums text-zinc-600">{n.attempt || 1}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-zinc-500 tabular-nums">{formatTs(n.sent_at)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {n.status === "sent" ? (
                        <span className="text-[10px] uppercase tracking-[0.16em] border border-emerald-300 text-emerald-700 bg-emerald-50 px-1.5 py-0.5">Sent</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-[0.16em] border border-red-300 text-red-700 bg-red-50 px-1.5 py-0.5" title={n.error || undefined}>Failed</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {n.status === "failed" && (
                        <button onClick={() => onResend(n.id)} disabled={busy}
                                className="text-[10px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-2 py-1 inline-flex items-center gap-1"
                                data-testid={`mm-notif-resend-${n.id}`}>
                          <RefreshCcw size={10} /> Resend
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}